import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, BookOpen, Users, Video, CreditCard, Settings, GraduationCap, Shield, Calendar, Clock, FileText, CheckCircle2, UserPlus } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-10 break-inside-avoid">
      <div className="flex items-center gap-3 mb-4 border-b-2 border-[#8B1538] pb-3">
        <div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-[#8B1538]" />
        </div>
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-[#1A1A1A]">{title}</h2>
      </div>
      <div className="text-[#4A4A4A] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function Step({ num, title, children }) {
  return (
    <div className="flex gap-4 mb-4 break-inside-avoid">
      <div className="h-8 w-8 rounded-full bg-[#8B1538] text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">{num}</div>
      <div>
        <h4 className="font-semibold text-[#1A1A1A] mb-1">{title}</h4>
        <p className="text-sm text-[#4A4A4A]">{children}</p>
      </div>
    </div>
  );
}

export default function ManualPage() {
  const contentRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header - hidden on print */}
      <div className="print:hidden sticky top-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-[#E5DFD3]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <ArrowLeft className="h-5 w-5 text-[#4A4A4A]" />
            <img src={LOGO_URL} alt="LVC" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-heading text-lg font-semibold text-[#8B1538]">LVC Misiones</span>
          </Link>
          <Button data-testid="download-manual-btn" onClick={handlePrint} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
            <Download className="h-4 w-4 mr-2" /> Descargar PDF
          </Button>
        </div>
      </div>

      {/* Manual Content */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-6 py-10 print:py-4 print:px-8">
        {/* Cover */}
        <div className="text-center mb-12 pb-8 border-b-4 border-[#8B1538]">
          <img src={LOGO_URL} alt="LVC Misiones" className="h-24 w-24 rounded-full object-cover mx-auto mb-6 print:h-20 print:w-20" />
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#8B1538] mb-2">Manual de Usuario</h1>
          <p className="text-xl text-[#1A1A1A] font-medium">LVC Misiones — La Voz de Cristo Para Las Naciones</p>
          <p className="text-[#737373] mt-2">Plataforma de Formacion Misionera Online</p>
          <p className="text-sm text-[#737373] mt-4">Version 1.0 — Abril 2026</p>
        </div>

        {/* Table of Contents */}
        <div className="mb-12 p-6 bg-[#F5F2EA] rounded-lg print:bg-gray-50 break-inside-avoid">
          <h2 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-4">Contenido</h2>
          <ol className="space-y-2 text-sm text-[#4A4A4A]">
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">1.</span> Introduccion</li>
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">2.</span> Registro e Inicio de Sesion</li>
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">3.</span> Guia del Estudiante</li>
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">4.</span> Guia del Docente</li>
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">5.</span> Guia del Administrador</li>
            <li className="flex items-center gap-2"><span className="font-bold text-[#8B1538]">6.</span> Preguntas Frecuentes</li>
          </ol>
        </div>

        {/* 1. Introduction */}
        <Section icon={BookOpen} title="1. Introduccion">
          <p>
            <strong>LVC Misiones</strong> es una plataforma de educacion misionera online que permite a estudiantes acceder a programas de formacion con clases en vivo, material de estudio, grabaciones y certificados de finalizacion.
          </p>
          <p>La plataforma cuenta con tres roles de usuario:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Estudiante:</strong> Se inscribe en modulos, asiste a clases, ve grabaciones y obtiene certificados.</li>
            <li><strong>Docente:</strong> Gestiona los cursos asignados, programa clases en vivo y agrega grabaciones.</li>
            <li><strong>Administrador:</strong> Crea modulos, asigna docentes, gestiona usuarios, inscripciones y pagos.</li>
          </ul>
          <div className="bg-[#8B1538]/5 border border-[#8B1538]/20 rounded-md p-4 mt-4">
            <p className="text-sm"><strong>Estructura del programa:</strong> Los <em>Modulos</em> contienen varios <em>Cursos</em>. Cada curso tiene un docente asignado, duracion y frecuencia de clases. Los cursos se desbloquean en orden: debes completar el curso anterior para acceder al siguiente.</p>
          </div>
        </Section>

        {/* 2. Registration */}
        <Section icon={Shield} title="2. Registro e Inicio de Sesion">
          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3">Registrarse</h3>
          <Step num="1" title="Ir a la pagina de registro">
            Desde la pagina principal, haz clic en el boton <strong>"Registrarse"</strong> en la esquina superior derecha.
          </Step>
          <Step num="2" title="Completar el formulario">
            Ingresa tu nombre completo, correo electronico y una contrasena segura. Selecciona tu rol (Estudiante o Profesor).
          </Step>
          <Step num="3" title="Confirmar">
            Haz clic en <strong>"Crear Cuenta"</strong>. Seras redirigido a tu panel de control.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Iniciar Sesion</h3>
          <Step num="1" title="Ir al login">
            Haz clic en <strong>"Iniciar Sesion"</strong> desde la pagina principal.
          </Step>
          <Step num="2" title="Ingresar credenciales">
            Escribe tu correo electronico y contrasena registrados.
          </Step>
          <Step num="3" title="Acceder">
            Haz clic en <strong>"Iniciar Sesion"</strong>. Seras llevado automaticamente a tu panel segun tu rol.
          </Step>
        </Section>

        {/* 3. Student Guide */}
        <Section icon={GraduationCap} title="3. Guia del Estudiante">
          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3">Panel de Estudiante</h3>
          <p>Tu panel muestra un resumen de tu actividad: modulos inscritos, progreso general y proximas clases.</p>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Inscribirse en un Modulo</h3>
          <Step num="1" title="Explorar modulos">
            Ve a <strong>"Explorar Modulos"</strong> en el menu lateral. Veras los programas disponibles con su precio y descripcion.
          </Step>
          <Step num="2" title="Ver detalles">
            Haz clic en <strong>"Ver Detalles"</strong> para ver la informacion completa del modulo: cursos incluidos, docentes, duracion y precio.
          </Step>
          <Step num="3" title="Inscribirse">
            Haz clic en <strong>"Inscribirse Ahora"</strong>. Selecciona tu plan de pago (mensual o anual) y confirma. Tambien el administrador puede inscribirte manualmente si pagaste fuera de la plataforma.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Asistir a Clases en Vivo</h3>
          <Step num="1" title="Ver proximas clases">
            En tu <strong>Dashboard</strong> veras las clases programadas. Tambien puedes verlas dentro del detalle del modulo.
          </Step>
          <Step num="2" title="Ingresar a la clase">
            Cuando la clase este por comenzar, haz clic en <strong>"Unirse"</strong>. Se abrira la sala de video en vivo (Jitsi Meet) directamente en la plataforma.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Ver Grabaciones y Marcar Progreso</h3>
          <Step num="1" title="Acceder a las grabaciones">
            Dentro del detalle del modulo, haz clic en cada <strong>curso</strong> para expandirlo y ver las grabaciones disponibles.
          </Step>
          <Step num="2" title="Ver la grabacion">
            Haz clic en <strong>"Ver"</strong> para abrir el enlace de la grabacion (YouTube o Vimeo).
          </Step>
          <Step num="3" title="Marcar como completada">
            Despues de ver la grabacion, haz clic en el <strong>check</strong> para marcarla como vista. Tu progreso se actualizara automaticamente.
          </Step>

          <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/20 rounded-md p-4 mt-4">
            <p className="text-sm flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-[#2D6A4F] flex-shrink-0 mt-0.5" /> <span><strong>Desbloqueo secuencial:</strong> Los cursos se desbloquean en orden. Completa todas las grabaciones del curso actual (100%) para desbloquear el siguiente.</span></p>
          </div>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Obtener tu Certificado</h3>
          <p>Cuando completes el 100% de todos los cursos del modulo, aparecera un boton para <strong>generar tu certificado</strong> de finalizacion. El certificado incluye tu nombre, el nombre del programa y un numero unico de verificacion.</p>
        </Section>

        {/* 4. Teacher Guide */}
        <Section icon={Video} title="4. Guia del Docente">
          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3">Panel de Docente</h3>
          <p>Tu panel muestra un resumen de tus cursos asignados, cantidad de estudiantes, clases programadas e ingresos.</p>

          <div className="bg-[#B8860B]/10 border border-[#B8860B]/20 rounded-md p-4 mt-2 mb-4">
            <p className="text-sm"><strong>Nota:</strong> Los cursos son asignados por el administrador. No puedes crear cursos, solo gestionar los que te fueron asignados.</p>
          </div>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Mis Cursos Asignados</h3>
          <p>En <strong>"Mis Cursos"</strong> veras la lista de cursos que te asigno el administrador, con informacion de duracion, clases por semana y cantidad de clases creadas.</p>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Programar una Clase en Vivo</h3>
          <Step num="1" title="Ir a Clases en Vivo">
            Haz clic en <strong>"Clases en Vivo"</strong> en el menu lateral.
          </Step>
          <Step num="2" title="Crear nueva clase">
            Haz clic en <strong>"Nueva Clase"</strong>. Selecciona el curso, escribe el titulo, elige fecha/hora y duracion.
          </Step>
          <Step num="3" title="Iniciar la clase">
            Cuando llegue la hora, haz clic en <strong>"Iniciar"</strong> para abrir la sala de video y comenzar la clase.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Agregar Grabacion</h3>
          <Step num="1" title="Ubicar la clase pasada">
            Las clases que ya pasaron muestran un boton <strong>"Grabacion"</strong>.
          </Step>
          <Step num="2" title="Agregar enlace">
            Haz clic en el boton, pega el enlace de YouTube o Vimeo de la grabacion y un titulo descriptivo. Guarda los cambios.
          </Step>
          <p className="text-sm mt-2">Los estudiantes podran ver la grabacion y marcarla como completada para avanzar en su progreso.</p>
        </Section>

        {/* 5. Admin Guide */}
        <Section icon={Settings} title="5. Guia del Administrador">
          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3">Panel de Administrador</h3>
          <p>El panel muestra estadisticas generales: usuarios, modulos, inscripciones, ingresos totales.</p>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Gestion de Modulos</h3>
          <Step num="1" title="Crear un modulo">
            Ve a <strong>"Modulos"</strong> en el menu. Haz clic en <strong>"Nuevo Modulo"</strong>. Ingresa titulo, descripcion, precio mensual y precio anual.
          </Step>
          <Step num="2" title="Agregar cursos al modulo">
            Dentro de cada modulo, haz clic en <strong>"+ Agregar Curso"</strong>. Define: titulo, descripcion, docente asignado, duracion (semanas), clases por semana y orden.
          </Step>
          <Step num="3" title="Cambiar docente">
            Haz clic en el icono de <strong>lapiz</strong> junto a cualquier curso para cambiar el docente, la duracion o el orden.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Gestion de Usuarios</h3>
          <p>En <strong>"Usuarios"</strong> puedes ver todos los usuarios registrados y cambiar su rol (Estudiante, Profesor, Admin).</p>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Inscripciones Manuales</h3>
          <Step num="1" title="Abrir seccion">
            Ve a <strong>"Inscripciones"</strong> en el menu lateral.
          </Step>
          <Step num="2" title="Nueva inscripcion">
            Haz clic en <strong>"Nueva Inscripcion"</strong>. Busca al estudiante por nombre o email, selecciona el modulo y el tipo de pago.
          </Step>
          <Step num="3" title="Confirmar">
            El sistema registra el pago como <strong>"pago externo"</strong> y activa el acceso del estudiante. La vigencia se calcula automaticamente: <strong>30 dias</strong> para mensual, <strong>365 dias</strong> para anual.
          </Step>

          <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 mt-6">Gestion de Pagos</h3>
          <p>En <strong>"Pagos"</strong> puedes ver el historial completo de pagos, incluyendo nombre del estudiante, modulo, monto, tipo y estado.</p>
        </Section>

        {/* 6. FAQ */}
        <Section icon={FileText} title="6. Preguntas Frecuentes">
          <div className="space-y-4">
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Como puedo recuperar mi contrasena?</h4>
              <p className="text-sm">Contacta al administrador para que te ayude a restablecer tu contrasena.</p>
            </div>
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Puedo ver las grabaciones en cualquier momento?</h4>
              <p className="text-sm">Si, mientras tu inscripcion este activa puedes acceder a todas las grabaciones disponibles dentro de los cursos desbloqueados.</p>
            </div>
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Como se desbloquean los cursos?</h4>
              <p className="text-sm">Los cursos se desbloquean en orden secuencial. Debes completar todas las grabaciones del curso actual (marcandolas como vistas) para desbloquear el siguiente.</p>
            </div>
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Cuando obtengo mi certificado?</h4>
              <p className="text-sm">Al completar el 100% de todos los cursos del modulo, aparecera automaticamente la opcion de generar tu certificado.</p>
            </div>
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Que pasa si pago fuera de la plataforma?</h4>
              <p className="text-sm">El administrador puede inscribirte manualmente desde la seccion "Inscripciones Manuales". Contacta al admin con tu comprobante de pago.</p>
            </div>
            <div className="break-inside-avoid">
              <h4 className="font-semibold text-[#1A1A1A]">¿Cuantas personas pueden estar en una clase en vivo?</h4>
              <p className="text-sm">La plataforma soporta hasta 75-100 participantes simultaneos por clase en vivo.</p>
            </div>
          </div>
        </Section>

        {/* Footer */}
        <div className="border-t-2 border-[#8B1538] pt-6 mt-12 text-center">
          <img src={LOGO_URL} alt="LVC" className="h-12 w-12 rounded-full object-cover mx-auto mb-3" />
          <p className="font-heading text-lg text-[#8B1538] font-semibold">La Voz de Cristo Para Las Naciones</p>
          <p className="text-sm text-[#737373]">Manual de Usuario — LVC Misiones v1.0</p>
          <p className="text-xs text-[#737373] mt-1">© {new Date().getFullYear()} Todos los derechos reservados</p>
        </div>
      </div>
    </div>
  );
}
